import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import ChartPieIcon from '@atlaskit/icon/core/chart-pie';
import Tooltip from '@atlaskit/tooltip';
import { ButtonItem } from '@atlaskit/menu';
import { useSidebar } from '@atlassian/jira-business-app-wrapper/src/controllers/sidebar/index.tsx';
import type { BusinessIssueField } from '@atlassian/jira-business-entity-project/src/services/issue-types-and-fields/types.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { useFlagsService } from '@atlassian/jira-flags';
import { useIntl } from '@atlassian/jira-intl';
import {
	type UIAnalyticsEvent,
	fireUIAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { ThemedButton } from '@atlassian/jira-project-theme-components/src/ui/themed-button/ThemedButton.tsx';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { useQueryParam } from '@atlassian/react-resource-router';
import { fg } from '@atlassian/jira-feature-gating';
import messages from './messages.tsx';
import { StatisticsSidebar } from './statistics-sidebar/index.tsx';
import { filterStatisticFields } from './utils.tsx';

export const ISSUE_FIELD_STATISTICS_SIDEBAR_CONTENT_KEY = 'issueFieldStatisticsSidebar';

type Props = {
	displayMenuButton?: boolean;
	jql: string;
	visibleFields: BusinessIssueField[];
	stopPulse?: () => void;
};

export const IssueFieldStatisticsButton = ({
	displayMenuButton,
	jql,
	visibleFields,
	stopPulse,
}: Props) => {
	const { formatMessage } = useIntl();
	const { showFlag, dismissFlag } = useFlagsService();
	const [{ content }, { setSidebarContent, closeSidebar }] = useSidebar();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const selectableFields = useMemo(() => filterStatisticFields(visibleFields), [visibleFields]);
	const [sidebar] = useQueryParam('sidebar');

	// detects whether the statistics sidebar is rendered, based on the content rendered within the sidebar
	const isStatisticsSidebarOpen = useMemo(
		() => content?.key === ISSUE_FIELD_STATISTICS_SIDEBAR_CONTENT_KEY,
		[content],
	);

	const onCloseSidebar = useCallback(() => {
		closeSidebar();
	}, [closeSidebar]);

	const onError = useCallback(() => {
		const flagId = showFlag({
			type: 'error',
			title: formatMessage(messages.errorFlagTitle),
			description: formatMessage(messages.errorFlagDescription),
			messageId: 'business-issue-field-statistics.ui.show-flag.error',
			messageType: 'transactional',
			actions: [
				{
					content: formatMessage(messages.errorFlagContactSupportActionText),
					href: 'https://support.atlassian.com/contact',
					target: '_blank',
					onClick: () => flagId && dismissFlag(flagId),
				},
			],
		});
		onCloseSidebar();
	}, [onCloseSidebar, showFlag, dismissFlag, formatMessage]);

	const renderStatisticsSidebar = useCallback(() => {
		setSidebarContent(
			<JSErrorBoundary
				key={ISSUE_FIELD_STATISTICS_SIDEBAR_CONTENT_KEY}
				id="sidebarContent"
				packageName="jiraBusinessIssueFieldStatistics"
				teamName="helios-reports"
				onError={onError}
			>
				<StatisticsSidebar selectableFields={selectableFields} jql={jql} onClose={onCloseSidebar} />
			</JSErrorBoundary>,
		);
	}, [onError, selectableFields, jql, onCloseSidebar, setSidebarContent]);

	const hasAutoOpened = useRef(false);
	useEffect(() => {
		if (!hasAutoOpened.current && sidebar === 'statistics' && !isStatisticsSidebarOpen) {
			hasAutoOpened.current = true;
			renderStatisticsSidebar();

			fireUIAnalytics(
				createAnalyticsEvent({}),
				'link clicked',
				'jira.issueFieldStatistics.openedSharedLink',
			);
		}
	}, [createAnalyticsEvent, sidebar, isStatisticsSidebarOpen, renderStatisticsSidebar]);

	const onClick = (_: React.MouseEvent, analyticsEvent: UIAnalyticsEvent) => {
		if (stopPulse) {
			stopPulse();
		}
		if (!isStatisticsSidebarOpen) {
			renderStatisticsSidebar();
			fireUIAnalytics(analyticsEvent, 'openIssueFieldStatistics');
		} else {
			closeSidebar();
		}
	};

	const onMenuClick = useCallback(() => {
		if (stopPulse) {
			stopPulse();
		}
		if (!isStatisticsSidebarOpen) {
			renderStatisticsSidebar();
			fireUIAnalytics(createAnalyticsEvent({}), 'openIssueFieldStatistics');
		} else {
			closeSidebar();
		}
	}, [
		stopPulse,
		isStatisticsSidebarOpen,
		renderStatisticsSidebar,
		createAnalyticsEvent,
		closeSidebar,
	]);

	if (displayMenuButton) {
		return (
			<ButtonItem
				key="issueFieldStatisticsMe"
				interactionName="openIssueFieldStatistics"
				onClick={onMenuClick}
				isSelected={isStatisticsSidebarOpen}
				aria-label={formatMessage(messages.tooltip)}
				testId="business-issue-field-statistics.ui.themed-button"
			>
				{formatMessage(messages.tooltip)}
			</ButtonItem>
		);
	}

	const themedButton = (
		<ThemedButton
			onClick={onClick}
			iconBefore={
				isVisualRefreshEnabled() ? (
					<ChartPieIcon label="" spacing="spacious" color="currentColor" />
				) : (
					<ChartPieIcon label="" color="currentColor" />
				)
			}
			isSelected={isStatisticsSidebarOpen}
			aria-label={isVisualRefreshEnabled() ? formatMessage(messages.tooltip) : undefined}
			appearance={isVisualRefreshEnabled() ? 'default' : 'subtle'}
			label={formatMessage(messages.tooltip)}
			title={
				isVisualRefreshEnabled() && !fg('visual-refresh_drop_5')
					? formatMessage(messages.tooltip)
					: undefined
			}
		>
			{!isVisualRefreshEnabled() && formatMessage(messages.chartButtonLabel)}
		</ThemedButton>
	);

	return isVisualRefreshEnabled() && fg('visual-refresh_drop_5') ? (
		<Tooltip content={formatMessage(messages.tooltip)}>{themedButton}</Tooltip>
	) : (
		themedButton
	);
};
